import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import api, { API_BASE } from '../api';
import GalleryViewer from '../components/GalleryViewer';
import SEO from '../components/SEO';
import StructuredData, { createNewsArticleSchema, createBreadcrumbSchema } from '../components/StructuredData';
import './NewsDetailPage.css';

function NewsDetailPage() {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaError, setMediaError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fromApproval = location.state && location.state.fromApproval;
  const incrementedRef = useRef(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        // If the parent navigated here with state (e.g., superadmin viewing unapproved item), use that
        if (location.state && location.state.news) {
          const newsData = location.state.news;
          setNews(newsData);

          // Fetch related news from same category
          const relatedResponse = await api.get(`/api/news?category=${newsData.category}&limit=4`);
          if (relatedResponse.data.success) {
            setRelatedNews(relatedResponse.data.data.filter((item) => item.slug !== (newsData.slug || slug)));
          }
          // Increment views for state-provided news (run once per mount)
          if (!incrementedRef.current) {
            incrementedRef.current = true;
            api.post(`/api/news/${newsData._id}/view`).then(() => {
              setNews(prev => prev ? { ...prev, views: (prev.views || 0) + 1 } : prev);
            }).catch(() => {});
          }
        } else {
          const response = await api.get(`/api/news/${slug}`);
          if (response.data.success) {
            const newsData = response.data.data;
            setNews(newsData);

            // Increment views for this open (run once per mount)
            if (!incrementedRef.current) {
              incrementedRef.current = true;
              api.post(`/api/news/${newsData._id}/view`).then(() => {
                setNews(prev => prev ? { ...prev, views: (prev.views || 0) + 1 } : prev);
              }).catch(() => {});
            }

            // Fetch related news from same category
            const relatedResponse = await api.get(`/api/news?category=${newsData.category}&limit=4`);
            if (relatedResponse.data.success) {
              setRelatedNews(relatedResponse.data.data.filter((item) => item.slug !== slug));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('hi-IN', options);
  };

  const makeSrc = (p) => {
    if (!p) return (API_BASE || '') + '/placeholder.png';
    if (/^https?:\/\//i.test(p)) return p;
    const path = p.startsWith('/') ? p : '/' + p;
    return (API_BASE || '') + path;
  };

  const isAudioUrl = (p) => /\.(mp3|wav|ogg|m4a|aac)$/i.test(p || '');

  const getCategoryLabel = (item) => {
    const raw = String(item?.category || '').trim();
    const lower = raw.toLowerCase();
    if (item?.isGallery || lower.includes('gallery')) return 'RR GALLERY';
    if (item?.isEvent || lower.includes('event')) return 'RR EVENTS';
    if (item?.isUjala || lower.includes('ujala') || lower.includes('moradabad')) return 'RR NEWS TV';
    return raw || 'RR NEWS TV';
  };

  const getCategoryRoute = (item) => {
    if (item?.isGallery) return '/category/ujala-gallery';
    if (item?.isEvent) return '/category/ujala-events';
    if (item?.isUjala) return '/category/ujala';
    const raw = String(item?.category || '').trim().toLowerCase();
    return `/category/${encodeURIComponent(raw.replace(/\s+/g, '-'))}`;
  };

  const getSourceLabel = (item) => {
    const raw = String(item?.source || '').trim();
    const lower = raw.toLowerCase();
    if (!raw || lower.includes('ujala') || lower.includes('moradabad')) return 'RR NEWS TV';
    return raw;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="container">
        <div className="error">खबर नहीं मिली।</div>
      </div>
    );
  }

  // Prepare SEO data
  const newsUrl = `/news/${news.slug || slug}`;
  const categoryLabel = getCategoryLabel(news);
  const categoryRoute = getCategoryRoute(news);
  const sourceLabel = getSourceLabel(news);
  const imageUrl = makeSrc(news.imageUrl || news.videoUrl);
  const truncatedDescription = news.description?.substring(0, 160) || '';
  
  // Create structured data schemas
  const articleSchema = createNewsArticleSchema({
    title: news.title,
    description: news.description,
    imageUrl: imageUrl,
    author: news.author,
    publishedDate: news.createdAt,
    modifiedDate: news.updatedAt,
    category: news.category,
    url: newsUrl,
  });
  
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'होम', url: '/' },
    { name: categoryLabel, url: categoryRoute },
    { name: news.title }
  ]);

  return (
    <div className="news-detail-page">
      {/* SEO Meta Tags */}
      <SEO
        title={`${news.title} - RR NEWS TV FASTEST UPDATE`}
        description={truncatedDescription}
        keywords={`${news.category}, ${news.title}, RR NEWS TV FASTEST UPDATE, hindi news`}
        author={news.author}
        image={imageUrl}
        url={newsUrl}
        type="article"
        publishedTime={news.createdAt}
        modifiedTime={news.updatedAt}
        section={news.category}
        tags={[news.category, 'RR News', 'Fastest Update']}
      />
      
      {/* Structured Data */}
      <StructuredData data={articleSchema} />
      <StructuredData data={breadcrumbSchema} />
      
      <div className="container">
        <article className="news-article">
          <div className="article-header">
            <span className="article-category">{categoryLabel}</span>
            {news.isBreaking && <span className="breaking-badge">⚡ ब्रेकिंग न्यूज़</span>}
          </div>

          <h1 className="article-title">{news.title}</h1>

          <div className="article-meta">
            <span className="meta-item">✍️ {news.author}</span>
            <span className="meta-item">📅 {formatDate(news.createdAt)}</span>
            <span className="meta-item">👁️ {news.views.toLocaleString('hi-IN')} Views</span>
          </div>

          {(news.isEvent || news.eventDate || news.eventVenue) && (
            <div className="event-panel" style={{marginTop:8,marginBottom:12,padding:10,background:'#fff7ea',borderRadius:6,border:'1px solid #ffe0b2'}}>
              <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                <strong style={{color:'#b35f00'}}>📣 इवेंट जानकारी</strong>
                {news.eventDate && <span style={{color:'#333'}}>📅 {formatDate(news.eventDate)}</span>}
                {news.eventVenue && <span style={{color:'#333'}}>📍 {news.eventVenue}</span>}
              </div>
            </div>
          )}

          {/* Ujala marquee bar above the image (not overlay) */}
          {news.isUjala && (
            <div className="ujala-detail-marquee css-marquee" role="region" aria-label="RR शीर्षक">
              <div className="marquee-track">RR NEWS TV FASTEST UPDATE — खबर आपकी, नज़र हमारी</div>
            </div>
          )}

          <div className="article-image">
            {news.isGallery && Array.isArray(news.galleryImages) && news.galleryImages.length > 0 ? (
              <GalleryViewer images={news.galleryImages} alt={news.title} showThumbs={false} />
            ) : news.videoUrl ? (
              mediaError ? (
                news.imageUrl ? (
                  <img src={makeSrc(news.imageUrl)} alt={news.title} />
                ) : (
                  <div style={{padding:12,background:'#f7f7f7',border:'1px solid #e0e0e0',borderRadius:6}}>
                    यह वीडियो उपलब्ध नहीं है।
                  </div>
                )
              ) : isAudioUrl(news.videoUrl) ? (
                <audio
                  src={makeSrc(news.videoUrl)}
                  controls
                  preload="metadata"
                  onError={() => setMediaError(true)}
                  style={{width:'100%'}}
                />
              ) : (
                <video
                  src={makeSrc(news.videoUrl)}
                  controls
                  playsInline
                  preload="metadata"
                  onError={() => setMediaError(true)}
                  style={{width:'100%',height:'100%',objectFit:'cover'}}
                />
              )
            ) : (
              <img src={makeSrc(news.imageUrl)} alt={news.title} />
            )}
          </div>
          <div className="article-description">
            <p>{news.description}</p>
          </div>

          <div className="article-content">
            {news.content.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {news.tags && news.tags.length > 0 && (
            <div className="article-tags">
              <strong>टैग्स:</strong>
              {news.tags.map((tag, index) => (
                <span key={index} className="tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          <div className="article-footer">
            <p className="source">स्रोत: {sourceLabel}</p>
          </div>
          {/* Superadmin controls: Edit / Delete */}
          {fromApproval && localStorage.getItem('role') === 'superadmin' && (
            <div className="superadmin-controls">
              <button className="btn edit" onClick={() => navigate('/admin/upload', { state: { edit: true, news } })}>Edit</button>
              <button className="btn danger" onClick={async () => {
                if (!window.confirm('Delete this news item? This cannot be undone.')) return;
                try {
                  const token = localStorage.getItem('token');
                  const res = await api.delete(`/api/news/${news._id}`, { headers: { Authorization: 'Bearer ' + token } });
                  const data = res && res.data ? res.data : null;
                  if (data && data.success) {
                    alert('Deleted');
                    navigate('/superadmin/approval');
                  } else alert((data && data.message) || 'Delete failed');
                } catch (err) { alert(err.message || 'Request failed'); }
              }}>Delete</button>
            </div>
          )}
        </article>

        {relatedNews.length > 0 && (
          <section className="related-news">
            <h2 className="section-title">📰 संबंधित खबरें</h2>
            <div className="related-grid">
              {relatedNews.map((item) => (
                <Link key={item._id} to={`/news/${item.slug}`} className="related-card">
                  <img src={item.imageUrl} alt={item.title} />
                  <h3>{item.title}</h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default NewsDetailPage;


